import { MULTI_FILTER_ALL_VALUE, MultiFilterType } from "@flowtribe/ui";

import { FilterObjectWithParamValue } from "../../interfaces/filter-object.interface";
import { SavedFilter } from "../../interfaces/filter-preference.interface";

/** `MultiFilterType.valueType` of a filter that is picked from a list. */
export const MULTI_SELECT_VALUE_TYPE = "multiSelect";

/** Rows of the filter bar (`ui-multi-select-filter` `maxFilters`). */
export const MAX_FILTERS = 3;

/** Filter types shown before the type list scrolls (`ui-multi-select-filter` `filterTypeMaxVisibleItems`). */
export const FILTER_TYPE_MAX_VISIBLE_ITEMS = 8;

/** Key prefix of an advanced label field's filter (`advLabel:<field>`); a customer holds its labels as `<field>:<label>`. */
export const ADVANCED_LABEL_FILTER_PREFIX = "advLabel:";

export function emptyFilterRow(): FilterObjectWithParamValue {
  return { label: "all", value: "", paramValue: "" };
}

/** The "All" state of a multi-select row picks nothing. */
export function getPickedValues(row: FilterObjectWithParamValue): string[] {
  return (row.values ?? []).filter(value => value !== MULTI_FILTER_ALL_VALUE);
}

/** Does the row filter on anything: a searched keyword (`paramValue`, not just a typed `value`) or at least one picked value? */
export function isFilterActive(row: FilterObjectWithParamValue): boolean {
  return !!row.paramValue || getPickedValues(row).length > 0;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** Only the separator is escaped: other list screens read this param without decoding it. */
function encodeKeyword(keyword: string): string {
  return keyword.replace(/:/g, "%3A");
}

function decodeKeyword(keyword: string): string {
  return keyword.replace(/%3A/gi, ":");
}

function keywordValue(keyword: string): Pick<FilterObjectWithParamValue, "value" | "paramValue"> {
  const trimmed = keyword.trim();
  return { value: trimmed, paramValue: trimmed };
}

/**
 * URL form of the rows, `key:value:key:value`. Multi-select rows are `key:value1,value2` with the key and every
 * value URI-encoded, because values such as the advanced label `Kantoor:Antwerpen` hold the separators.
 * Keyword rows are `key:keyword` with any ":" in the keyword escaped as %3A.
 */
export function serializeFilters(rows: FilterObjectWithParamValue[]): string {
  return rows
    .filter(isFilterActive)
    .map(row => {
      const pickedValues = getPickedValues(row);
      return pickedValues.length
        ? `${encodeURIComponent(row.label)}:${pickedValues.map(encodeURIComponent).join(",")}`
        : `${row.label}:${encodeKeyword(row.paramValue)}`;
    })
    .join(":");
}

/** Inverse of `serializeFilters`. An old keyword value (`labels:vip`) of a multi-select filter reads as one picked value. */
export function parseFilters(filtersString: string, filterTypes: MultiFilterType[]): FilterObjectWithParamValue[] {
  if (!filtersString) {
    return [];
  }
  const parts = filtersString.split(":");
  const rows: FilterObjectWithParamValue[] = [];
  for (let index = 0; index < parts.length && rows.length < MAX_FILTERS; index += 2) {
    const label = safeDecode(parts[index]);
    const rawValue = parts[index + 1] || "";
    const isMultiSelect = filterTypes.find(type => type.id === label)?.valueType === MULTI_SELECT_VALUE_TYPE;
    rows.push(
      isMultiSelect
        ? { label, value: "", paramValue: "", values: rawValue ? rawValue.split(",").map(safeDecode).filter(Boolean) : [] }
        : { label, ...keywordValue(decodeKeyword(rawValue)) }
    );
  }
  return rows;
}

/**
 * The `filters` request param, e.g. `[{"name":"john"},{"labels":["vip","demo"]}]`. An empty text when nothing
 * filters: `undefined` would be sent as the text "undefined".
 */
export function buildFiltersParam(rows: FilterObjectWithParamValue[]): string {
  const filters = rows.filter(isFilterActive).map(row => {
    const pickedValues = getPickedValues(row);
    return { [row.label]: pickedValues.length ? pickedValues : row.paramValue };
  });
  return filters.length ? JSON.stringify(filters) : "";
}

/** A search takes what is typed in each keyword row; a multi-select row already holds its picked values. */
export function applyKeywordSearch(rows: FilterObjectWithParamValue[]): FilterObjectWithParamValue[] {
  return rows.map(row => (row.values ? row : { ...row, paramValue: row.value?.trim() ?? "" }));
}

export function dropEmptyKeywordRows(rows: FilterObjectWithParamValue[]): FilterObjectWithParamValue[] {
  const kept = rows.filter(row => row.values || row.paramValue);
  return kept.length ? kept : rows.slice(0, 1);
}

/** `ui-multi-select-filter` only notices a new `filterArray` reference, so a row is replaced, not edited. */
export function replaceFilterRow(
  rows: FilterObjectWithParamValue[],
  rowIndex: number,
  changes: Partial<FilterObjectWithParamValue>
): FilterObjectWithParamValue[] {
  return rows.map((row, index) => (index === rowIndex ? { ...row, ...changes } : row));
}

export function toSavedFilters(rows: FilterObjectWithParamValue[]): SavedFilter[] {
  return rows.filter(isFilterActive).map(row => ({ key: row.label, value: row.paramValue ?? "", values: row.values }));
}

export function validSavedFilters(saved: SavedFilter[] | undefined, filterTypes: MultiFilterType[]): SavedFilter[] {
  if (!Array.isArray(saved)) {
    return [];
  }
  const typesById = new Map(filterTypes.map(type => [type.id, type]));
  return saved.flatMap(entry => {
    const type = typesById.get(entry?.key);
    if (!type) {
      return [];
    }
    if (type.valueType !== MULTI_SELECT_VALUE_TYPE) {
      return entry.value ? [entry] : [];
    }
    const optionValues = new Set((type.options ?? []).map(option => option.item));
    const values = (entry.values ?? []).filter(value => optionValues.has(value));
    return values.length ? [{ ...entry, values }] : [];
  });
}

export function fromSavedFilters(saved: SavedFilter[]): FilterObjectWithParamValue[] {
  return saved.map(entry => ({ label: entry.key, value: entry.value, paramValue: entry.value, values: entry.values }));
}

/** Anything typed or picked counts, even before it is searched. */
export function hasActiveFilterRows(rows: FilterObjectWithParamValue[]): boolean {
  return rows.some(row => !!row.value?.trim() || isFilterActive(row));
}
